/**
 * Game objects (CR 120-127, 179-187; research 01 §2; docs/design/03 §3.1).
 *
 * A `GameObject` is one *incarnation* of a card, token, rune, legend or battlefield. Moving into or out
 * of a non-board zone creates a new incarnation with a new id and no damage, counters, statuses or
 * attachments (CR 124-124.1). Moving between board locations (base ↔ battlefield) keeps the object.
 *
 * Derived characteristics (current Might, keywords, costs, types, tags) are never stored: the rules
 * layer computes them from the card definition and continuous effects (docs/design/03 §9). Printed
 * data lives in `@play/catalog`; the table layer only needs the object's structural category.
 */
import type { Audience, CardId, ObjectId, PlayerId, PrintingId } from './ids';
import type { ZoneRef } from './zones';

/**
 * Structural category (CR 133.4-133.6; research 01 §2.2). It decides which zones an object can be in,
 * never rules behaviour (the rules layer reads card definitions for that):
 * - `card`: units, spells and gear: Main Deck cards (including the Chosen Champion) and unit/gear tokens;
 * - `rune`: Rune Deck cards;
 * - `legend`: the Champion Legend (and legends added by effects, CR 107.4.d.1);
 * - `battlefield`: battlefield cards, printed or token (locations, CR 199).
 *
 * Tokens (CR 179-187) are flagged with `isToken`: they cease to exist outside the board and the chain
 * (CR 186.1) and can never become cards (CR 185.1).
 */
export type ObjectCategory = 'card' | 'rune' | 'legend' | 'battlefield';

/** What a card shows: its rules identity and the art to display. */
export interface CardRef {
  /** Rules identity: an alias-resolved card id, or a `TokenId` (`token:recruit`) for tokens. */
  cardId: CardId;
  /** Art (cosmetic). `null` for tokens and cards without a printing; the client then uses a placeholder. */
  printingId: PrintingId | null;
}

/** Well-known statuses set by hand in tier 1 and by effects in tiers 2-3. Other strings are allowed. */
export const KNOWN_STATUSES = ['stunned', 'empowered'] as const;

/**
 * A status marker on a board object (CR 124.2): stunned (CR 424), empowered (CR 442), … Exhausted,
 * facedown, attached and revealed have dedicated fields instead.
 */
export interface Status {
  /** `stunned`, `empowered`, or another status name. */
  kind: string;
  /** Object whose effect applied it (tiers 2-3), `null` when set by hand. */
  source: ObjectId | null;
  /** When it expires (tiers 2-3; e.g. stuns end at the end of turn, CR 424.1.a.2). `null`: until removed. */
  until: 'endOfTurn' | 'endOfCombat' | null;
}

/** One game object incarnation (see the module header). */
export interface GameObject {
  /** Incarnation id; a new one on every move into or out of a non-board zone (CR 124). */
  id: ObjectId;
  category: ObjectCategory;
  /** A token (CR 179-187), not a card. */
  isToken: boolean;
  /** Rules identity (alias-resolved, research 03 §1.5); tokens use their `TokenId`. */
  cardId: CardId;
  /** Art to show (cosmetic only, never read by rules). */
  printingId: PrintingId | null;
  /** The player who brought it into the game or created it (CR 127.1, 183). Never changes. */
  owner: PlayerId;
  /** The player who controls it (CR 191). Equals the owner except after control changes. */
  controller: PlayerId;
  zone: ZoneRef;
  /** Logical time it became this incarnation; orders continuous effects (CR 480-482). */
  timestamp: number;
  /** Exhausted (turned sideways, CR 415) or ready (CR 416). */
  exhausted: boolean;
  /** Marked damage (CR 142). Never negative. */
  damage: number;
  /** Buff counters (CR 701-705). Never negative. */
  buffs: number;
  /** Other counters by kind (CR 742-749), e.g. custom counters set by hand in tier 1. Never negative. */
  counters: Record<string, number>;
  statuses: Status[];
  /** The unit this gear is attached to (CR 435-436, 716-719), or `null`. */
  attachedTo: ObjectId | null;
  /** Facedown on the board (CR 129.4): its front is private to its controller. */
  faceDown: boolean;
  /**
   * Who may see this object beyond its zone's default privacy: revealed cards (CR 425), cards a player
   * looked at (CR 437). Reset to `[]` with every new incarnation and when its deck is shuffled.
   */
  revealedTo: Audience;
  /** Linked objects by role, e.g. "the card banished with this" (tiers 2-3; research 03 §9.7). */
  links: Record<string, ObjectId[]>;
}

/** Whether the object is a token (CR 185.1). */
export function isToken(object: GameObject): boolean {
  return object.isToken;
}

/** The object's face: card id and printing. */
export function cardRefOf(object: GameObject): CardRef {
  return { cardId: object.cardId, printingId: object.printingId };
}

/** Whether the object has a status of this kind. */
export function hasStatus(object: GameObject, kind: string): boolean {
  return object.statuses.some((s) => s.kind === kind);
}
